<template>
  <div
    v-tooltip.once="tooltip"
    :class="bubbleClasses"
    :data-testid="variant === 'default' ? 'default-bubble' : 'unlinked-bubble'"
    :style="bubbleStyle"
    @click="handleClick"
  >
    {{ ordinal }}
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useBubbleStore } from '@app/application/communication/store/bubbles'
import {
  scrollToElementWithOffset,
  scrollToElement,
} from '@app/utils/domHelpers'
import trackEvent from '@app/utils/track_event'
import useI18n from '@app/shared/vue/composables/use-I18n'

interface Props {
  correspondingRemarkId?: string
  emitClick?: boolean
  linkingPath: string
  offsetRight?: number
  ordinal?: string
  position?: 'static' | 'absolute'
  type: 'liquid-template' | 'sidepanel'
  variant?: 'default' | 'unlinked'
}

const props = withDefaults(defineProps<Props>(), {
  correspondingRemarkId: undefined,
  offsetRight: 0,
  ordinal: '',
  position: 'static',
  variant: 'default',
})

const emit =
  defineEmits<{
    click: [Event]
  }>()

const bubbleStore = useBubbleStore()
const { scrollOffset } = storeToRefs(bubbleStore)

const I18n = useI18n()

const bubbleClasses = computed(() => ({
  'tw-inline-flex': true,
  'tw-min-w-4': true,
  'tw-h-[14px]': true,
  'tw-min-h-[14px]': true,
  'tw-text-[8px]': true,
  'tw-leading-[8px]': true,
  'tw-justify-center': true,
  'tw-items-center': true,
  'tw-rounded-t-full': true,
  'tw-rounded-r-full': true,
  'tw-select-none': true,
  'tw-cursor-pointer': props.variant === 'default',
  'tw-cursor-not-allowed': props.variant === 'unlinked',
  'tw-bg-sf-primary-dark': props.variant === 'default',
  'tw-bg-sf-gray-lighter': props.variant === 'unlinked',
  'tw-text-white': true,
  'tw-absolute': props.position === 'absolute',
  'tw-top-0': props.position === 'absolute',
  'tw-right-0': props.position === 'absolute' && !props.offsetRight,
  '-tw-translate-y-1/2': props.position === 'absolute',
  'tw-translate-x-1/2': props.position === 'absolute',
  'tw-z-20': props.position === 'absolute',
}))

const bubbleStyle = computed(() => ({
  right: `${props.offsetRight}px`,
}))

const tooltip = computed(() => {
  return props.variant === 'unlinked'
    ? I18n.t('remarks.message_attached_to_input')
    : ''
})

function handleClick(event: Event): void {
  if (props.emitClick) {
    bubbleClickUp(event)
  } else {
    scrollToRemarkCard(event)
  }
}

function bubbleClickUp(event: Event): void {
  emit('click', event)
}

function scrollToRemarkCard(event: Event) {
  event.stopPropagation()
  event.preventDefault()

  trackEvent('input-comms', 'bubble-click', `from-${props.type}`)

  bubbleStore.setHighlightedBubble(props.linkingPath)

  if (props.type === 'sidepanel') {
    if (scrollOffset.value) {
      // Scroll to the place in the liquid template where the input field is
      scrollToElementWithOffset(
        `[data-name='${props.linkingPath}']`,
        scrollOffset.value
      )
    } else {
      scrollToElement(`[data-name='${props.linkingPath}']`, 'nearest')
    }
  }

  const elementId = props.correspondingRemarkId?.replace('remark-', '') || ''

  // Scroll to the communication sidepane specific remark
  window.requestAnimationFrame(() => {
    window.eventHub.$emit('scrollToElement', {
      elementType: 'remark',
      elementId,
      force: true,
    })
  })
}
</script>
